import { MatSort } from '@angular/material/sort';
import { Component, ViewChild } from '@angular/core';
import { BaseComponent } from '../../base.component';
import { map, Observable, shareReplay, Subject, switchMap } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { DiscoveryManager } from '../../../managers/discovery.manager';
import { SearchManager } from '../../../managers/search.manager';
import { keys, uniq } from 'lodash-es';
import { SelectFilterNewComponent } from '../../dialogs/select-filter-new/select-filter-new.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { filter, takeUntil } from 'rxjs/operators';
import { HelpService } from '../../../services/help.service';
import { Filter } from '../../../models/ui/filter.model';
import { AnalysisManager } from '../../../managers/analysis.manager';
import { KnownReports } from '../../../constants/known-reports';

@Component({
    selector: 'lex-case-assessment',
    templateUrl: './case-assessment.component.html',
    styleUrls: ['./case-assessment.component.scss'],
    standalone: false
})

export class CaseAssessmentComponent extends BaseComponent {
	@ViewChild(MatSort, { static: false }) sort: MatSort;

	public dataSource$: Observable<MatTableDataSource<Record<string, string>>>;

	public columnsToDisplay: string[] = [];
	public chartData: ChartSeries[];
	public xAxisLabel: string = 'Pivot';
	public yAxisLabel: string = 'Total';
	public filter: Filter = null;
	public viewMode: 'graph' | 'table' = 'graph';
	public reportType: KnownReports = 'upload-doc-count-by-extension';
	public customReportPrimaryField: { name: string, id: string };
	public customReportOptionalFieldId: string = '00000000-0000-0000-0000-000000000000';
	public customReportDateUnit: string = 'year-only';
	public customReportSumUnit: string = 'doc-count';
	public isChartDataLoaded: boolean = false;

	public caseAssessmentFields$ = this.analysisManager.caseAssessmentFields$;
	private refreshReportSource = new Subject<void>();

	constructor(
		private dialog: MatDialog,
		private searchManager: SearchManager,
		private discoveryManager: DiscoveryManager,
		private helpService: HelpService,
		private analysisManager: AnalysisManager
	) {
		super();
		this.helpService.updateHelpLinkUrl('https://help.ediscoveryplatform.com/home/help/analysis-menu/case-assessment');

		const sharedSource = this.refreshReportSource.pipe(
			switchMap(() => this.analysisManager.getCaseAssessmentReport(this.reportType, this.filter?.savedFilterId, {
				dateUnit: this.customReportDateUnit,
				sumUnit: this.customReportSumUnit,
				primaryFieldId: this.customReportPrimaryField?.id,
				optionalFieldId: this.customReportOptionalFieldId
			})),
			shareReplay(1)
		);

		this.dataSource$ = sharedSource.pipe(
			filter((reportData) => !!reportData),
			map((reportData) => {
				const source = new MatTableDataSource(reportData.data);
				setTimeout(() => source.sort = this.sort);
				return source;
			})
		);

		sharedSource.pipe(
			filter((reportData) => !!reportData),
			takeUntil(this.destroy$)
		).subscribe((r) => {
			let columns = r.data.reduce((p, r) => p.concat(keys(r)), []);
			columns = uniq(columns);
			this.columnsToDisplay = columns;
			this.xAxisLabel = this.reportType === 'custom' ? this.customReportPrimaryField?.name : r.pivotLabel;
			this.chartData = r.data.map((y) => {
				return {
					name: y.pivot ?? '(empty)',
					value: y.total
				};
			});
			this.isChartDataLoaded = true;
		});
		this.refreshReport();
	}

	public displayFilterDialog(): void {
		const dialogConfig = new MatDialogConfig();

		dialogConfig.autoFocus = true;
		dialogConfig.width = '800px';
		dialogConfig.data = {
			filter: this.filter ? JSON.parse(this.filter.filterJson) : null
		};

		const dialogRef = this.dialog.open(SelectFilterNewComponent, dialogConfig);

		dialogRef.afterClosed().subscribe(data => {
			if (!data || data.isCancelled) return;

			if (data.filter) {
				this.searchManager.createFilter({ filters: data.filter }).subscribe((newFilter) => {
					this.filter = newFilter;
					this.refreshReport();
				});
			} else {
				this.refreshReport();
			}
		});
	}

	public exportToExcel(): void {
		let customReportParams = null;
		if (this.reportType === 'custom') {
			customReportParams = {
				dateUnit: this.customReportDateUnit,
				sumUnit: this.customReportSumUnit,
				primaryFieldId: this.customReportPrimaryField?.id,
				optionalFieldId: this.customReportOptionalFieldId
			};
		}
		this.analysisManager.exportCaseAssessmentReport(this.reportType, this.filter?.savedFilterId, customReportParams);
	}

	public refreshReport(): void {
		if (this.reportType === 'custom' && !this.customReportPrimaryField) return;

		this.isChartDataLoaded = false;
		this.refreshReportSource.next();
	}

}

interface ChartSeries {
	name: string,
	value: string
}

